<template>
  <span>
    <div class="page-level-functions">
      <action-button
        variant="success float-right ml-3"
        text="Guardar"
        i="fas fa-save"
        @click="saveRolHandler"
      />
      <action-button
        variant="outline-success float-right ml-3"
        text="Regresar"
        i="fas fa-arrow-circle-left"
        @click="$router.go(-1)"
      />
    </div>
    <hr class="page-level-functions-separator" />
    <section-title
      content="Información del Rol"
      icon="fas fa-user"
      class="my-4 custom-section-title"
      container-class="bg-secondary-23"
    />
    <div class="mt-3">
      <b-tabs
        content-class="mt-3"
        active-nav-item-class="custom-tab custom-active-tab"
        nav-class="custom-nav-container"
        v-model="roleTabIndex"
      >
        <b-tab title="Información General" active>
          <validation-observer ref="rolFormRef">
            <div class="row">
              <div class="col-md-12 mb-4">
                <validation-provider :rules="'required'" v-slot="{ errors }">
                  <custom-input
                    v-model="model.roleName"
                    label="Nombre:"
                    type="text"
                    maxlength="100"
                    :error="errors[0]"
                    custom-error-msg="Campo Requerido."
                  />
                </validation-provider>
              </div>
              
              <div class="col-md-12 mt-3">
                <validation-provider
                  class="w-100"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <custom-text-area
                    label="Descripción:"
                    :maxlength="500"
                    :classes="['eq-field']"
                    v-model="model.roleDescription"
                    :error="errors[0]"
                    custom-error-msg="Campo Requerido."
                    name="comment"
                    id="comment"
                  />
                </validation-provider>
              </div>
            </div>
          </validation-observer>
          <div class="row">
            <div class="col-md-12 mt-3">
              <validation-provider
                ref="applicationMultiRef"
                :mode="selectionChangeValMode"
                :rules="'applicationsRequired:1'"
                v-slot="{ errors: roleErrors }"
              >
                <chips-selection
                  ref="roleSelection"
                  selected-items-title="Aplicaciones Seleccionadas"
                  v-model="model.applications"
                  @addingItem="addingApplication"
                  @removingItem="removingApplication"
                  :errors="roleErrors"
                  :chips-config="applicationChipsConfig"
                  class="pb-3"
                >
                  <template #selectionInputs>
                    <validation-observer ref="applicationRef">
                      <div class="form-group">
                        <label class="field-label">Aplicación</label>
                        <validation-provider
                          :rules="'required'"
                          v-slot="{ errors }"
                        >
                          <custom-drop-down
                            ref="appsDdl"
                            :options="availableApplicationsOptions"
                            fieldname="name"
                            fieldkey="id"
                            placeholder="Seleccione"
                            :error="errors[0]"
                            custom-error-msg="Favor seleccione."
                            :value="selectedApplication"
                            @input="onApplicationSelected($event)"
                          />
                        </validation-provider>
                      </div>
                    </validation-observer>
                  </template>
                </chips-selection>
              </validation-provider>
            </div>
          </div>
          <div class="row">
            <div class="col-md-4 mt-3">                                                            
              <div class="form-group">
                <label class="field-label">Clasificación de Rol</label>
                <validation-observer ref="roleTypeRef">
                  <validation-provider
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <custom-drop-down                            
                      :options="catalog.roleTypes"
                      fieldname="name"
                      fieldkey="id"
                      placeholder="Seleccione"
                      :error="errors[0]"
                      :clearable="false"
                      custom-error-msg="Favor seleccione."
                      :value="model.roleType"  
                      @input="onRoleTypeSelected($event)"                  
                    />
                    </validation-provider>          
                </validation-observer>
              </div>
            </div>
          </div>
        </b-tab>
        <b-tab title="Permisos">
          <permission-selection
            ref="permissionSelectionRef"
            :items="permissions"
            @on-select-permission="onSelectPermissionHandler"
          />
        </b-tab>
      </b-tabs>
    </div>
  </span>
</template>

<script>
import SectionTitle from '@/components/common/SectionTitle'
import { CustomTextArea } from '@/components/common'
import CustomDropDown from '@/components/common/form/CustomDropDown'
import ActionButton from '@/components/common/ActionButton'
import ChipsSelection from '@/components/common/ChipsSelection'
import PermissionSelection from '@/views/shared/PermissionSelection.vue'
import { getApplicationsList as _getApplicationsList } from '@/modules/applications/api.js'
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { getAccessLevelIds } from '@/utils/constants/accessLevel';
import {
  getRoleById as _getRoleById,
  saveRole as _saveRole,
  getRoleTypesList as _getRoleTypesList
} from '@/modules/roles/api.js'
import CustomInput from 'CustomInput';

extend('applicationsRequired', {
  validate(selectedApplications, args) {
    return {
      required: true,
      valid: selectedApplications?.length >= args.length,
    }
  },
  computesRequired: true,
  params: ['length'],
  message: 'Favor de seleccionar la aplicacion.',
})

export default {
  name: 'ManageRole',
  components: {
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomInput,
    CustomTextArea,
    CustomDropDown,
    ActionButton,
    PermissionSelection,
    ChipsSelection,
  },
  data: () => ({
    roleTabIndex: 0,
    model: {
      roleName: null,
      roleDescription: null,
      roleId: null,
      applications: [],
      roleType: null
    },
    permissions: [],
    addedPermission: [],
    catalog: {
      applications: [],
      roleTypes: []
    },
    selectedApplication: null,
    applicationChipsConfig: [{ labelText: 'Aplicación', valueProp: 'name' }],
    selectedApplications: [],
  }),
  computed: {
    availableApplicationsOptions() {
      const data = this.catalog.applications.filter(
        (a) => !this.model.applications?.some((e) => e.id == a.id)
      )
      return data
    },
    hasMultipleAccessLevel(){
      const accessLevelIds = getAccessLevelIds();
      const permissions = this.model?.permissions ?? this.permissions;
      const accessLevelPermissionsAdded = permissions.filter(permissionId => accessLevelIds.some(_permissionId => permissionId === _permissionId ))
      return accessLevelPermissionsAdded.length > 1; 
    },
  },
  mounted() {
    const { roleId } = this.$route.params
    this.callGetApplications()
    this.callGetRoleTypes()

    if (roleId) {
      this.callGetRoleById(roleId)
    }
  },
  methods: {
    async callGetApplications() {
      const data = await _getApplicationsList()
      this.catalog = {
        ...this.catalog,
        applications: data,
      }
    },
    async callGetRoleTypes() {
      const {data} = await _getRoleTypesList()      
      this.catalog = {
        ...this.catalog,
        roleTypes: data,
      }
    },
    async callGetRoleById(id) {
      const { data } = await _getRoleById(id)
      this.model = {
        ...this.model,
        ...data,
        applications: !data.applications ? [] : data.applications,
      }
    },
    reset() {
      this.$refs.permissionSelectionRef._data.searchText = ''
      this.roleTabIndex = 0
    },
    onSelectPermissionHandler(permissions) {
      this.model = {
        ...this.model,
        permissions,
      }
    },
    async saveRolHandler() {
      var isValid = await this.$refs.rolFormRef.validate()
      let applicationValidation = await this.$refs.applicationMultiRef.validate()
      let roleTypeValidation = await this.$refs.roleTypeRef.validate();
     
      if (!isValid || !applicationValidation.valid || !roleTypeValidation) {
        this.Toast(
          'Alerta',
          'Favor de completar la información solicitada para continuar.',
          'error'
        )
        return
      }

      if(this.hasMultipleAccessLevel){
        this.Toast(
          'Alerta',
          'Solo es permitido guardar un nivel de acceso.',
          'error'
        )
        return;
      }

      const payload = {
        ...this.model,
        applications: this.model.applications.map((s) => s.id),
      }
      const { data: saveResult } = await _saveRole(payload)

      if (saveResult) {
        this.Toast('Exito', 'El rol ha sido guardado exitosamente.', 'success')
        if (!this.model.roleId) {
          this.model = {}
          await this.$refs.rolFormRef.reset()
          await this.$refs.roleTypeRef.reset()
        }
        this.reset()
        this.$refs.applicationMultiRef.reset()
      }
    },
    async addingApplication() {
      var passed = await this.$refs.applicationRef.validate()
      if (passed) {
        const applicationToAdd = { ...this.selectedApplication }

        this.selectedApplication = null

        this.$refs.applicationRef.reset()
        return applicationToAdd
      }
    },
    removingApplication() {},
    selectionChangeValMode: () => {
      return {
        on: ['selectionChanged'],
      }
    },
    async onApplicationSelected(val) {
      if (!val) {
        this.selectedApplication = null
        return false
      }
      this.selectedApplication = val
    },
    onRoleTypeSelected(val)
    {     
      this.model.roleType = val;
    }
  },
}
</script>

<style scoped lang="scss">
@import '@/sass/config/_colors.scss';

::v-deep .custom-nav-container {
  border-bottom: 1px solid map-get($colors, 'gray-5');
  .nav-item {
    .nav-link {
      font-size: 18px;
      padding-right: 100px;
      color: map-get($colors, 'gray-11');
      padding-left: 100px;
      &.active {
        border-bottom: 3px solid map-get($colors, 'orange');
        color: map-get($colors, 'orange');
        font-weight: bold;
      }
      &.active:after {
        content: unset;
      }
    }
  }
}

::v-deep .custom-section-title {
  .tag-badge-info {
    background: map-get($colors, 'secondary-23') !important;
  }
}
</style>